<template>
  <Modal position="end" ref="modalRef">
    <template #header>Tour options</template>
    <template #body>
      <View direction="column" gap="lg">
        <Text variant="body-medium" color="secondary">
          Select the tour options you want to offer to potential tenants. At least one option must be selected.
        </Text>

        <FormGroup>
          <FormField label="Available tour types" direction="column">
            <View direction="column" gap="md">
              <CheckboxCard
                title="In-person tour"
                description="Showcasing your property to tenants personally or by a leasing agent"
                icon="user-01"
                v-model="form.fields.allowsInPersonTours"
              />
              <CheckboxCard
                title="Virtual tour"
                description="Showcasing your property to tenants online via apps like FaceTime or Zoom"
                icon="video-recorder"
                v-model="form.fields.allowsVirtualTours"
              />
              <CheckboxCard
                title="Self-guided tour"
                description="Showcasing your property to tenants by providing them with instructions to access the property"
                icon="key"
                v-model="form.fields.allowsSelfGuidedTours"
              />

              <!-- Sits with the tour cards, not below the access form: it applies to every tour type
                   the owner offers, so it is the decision made right after choosing how strangers
                   get to see the place. -->
              <IdentityVerificationOption v-if="hasTourType" v-model="form.fields.identityVerificationEnabled" />
            </View>
          </FormField>

          <SelfGuidedAccessForm
            ref="accessFormRef"
            v-if="form.fields.allowsSelfGuidedTours"
            v-model="form.fields.selfGuidedAccessMethod"
            :property-id="propertyStore.property?.id"
          />

          <Hint>At least one tour option must be selected to allow potential renters to view your property.</Hint>
        </FormGroup>
      </View>
    </template>
    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button @click="save" :loading="form.submitting" :disabled="!isFormValid">Save</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import {
  Button,
  CheckboxCard,
  FormField,
  FormGroup,
  Hint,
  Modal,
  Text,
  useAlertsStore,
  useForm,
  View,
} from '@tallo/design-system';
import { IdentityVerificationOption, SelfGuidedAccessForm, usePropertyStore } from '@tallo/investor/property';
import { SelfGuidedAccessMethod } from '@tallo/property';

interface TourOptionsForm {
  allowsInPersonTours: boolean;
  allowsVirtualTours: boolean;
  allowsSelfGuidedTours: boolean;
  selfGuidedAccessMethod: SelfGuidedAccessMethod | null;
  identityVerificationEnabled: boolean;
}

const propertyStore = usePropertyStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const accessFormRef = ref<InstanceType<typeof SelfGuidedAccessForm> | null>(null);

const form = useForm<TourOptionsForm>({
  allowsInPersonTours: propertyStore.property?.allowsInPersonTours ?? false,
  allowsVirtualTours: propertyStore.property?.allowsVirtualTours ?? false,
  allowsSelfGuidedTours: propertyStore.property?.allowsSelfGuidedTours ?? false,
  selfGuidedAccessMethod: propertyStore.property?.selfGuidedAccessMethod ?? null,
  identityVerificationEnabled: propertyStore.property?.identityVerificationEnabled ?? false,
});

const hasTourType = computed(
  () => form.fields.allowsInPersonTours || form.fields.allowsVirtualTours || form.fields.allowsSelfGuidedTours,
);

const isFormValid = computed(() => {
  if (!hasTourType.value) return false;

  return form.fields.allowsSelfGuidedTours ? Boolean(accessFormRef.value?.isValid) : true;
});

function populateForm() {
  const property = propertyStore.property;
  if (!property) return;

  form.fields.allowsInPersonTours = property.allowsInPersonTours ?? false;
  form.fields.allowsVirtualTours = property.allowsVirtualTours ?? false;
  form.fields.allowsSelfGuidedTours = property.allowsSelfGuidedTours ?? false;
  form.fields.selfGuidedAccessMethod = property.selfGuidedAccessMethod ?? null;
  form.fields.identityVerificationEnabled = property.identityVerificationEnabled ?? false;
}

watch(() => propertyStore.property, populateForm, { immediate: true });

/**
 * The access-instructions modal edits the same fields in place while this stays mounted, and the
 * watch above only fires when the whole property object is replaced — so the form is rebuilt from
 * the store on every open rather than trusting whatever it captured last time.
 */
function open() {
  populateForm();
  modalRef.value?.show();
}

async function save() {
  await form.submit(async (fields) => {
    await propertyStore.saveTourTypes(fields);
    await accessFormRef.value?.save();

    useAlertsStore().success('Tour options updated');
    close();
  });
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  open,
});
</script>
